import React, { Fragment, useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { CompleteCheckbox, ConfirmDialog } from "@activecollab/components";
import PropTypes from "prop-types";
import { completeTask, openTask } from "~/store/actions/actions.tasks.jsx";

const TaskCompleteCheckbox = ({
  taskId,
  defaultCompleted,
  disabled,
  className,
  parentDependenciesCount,
  openSubtasksCount,
  onComplete: externalOnComplete,
}) => {
  const [completed, setCompleted] = useState(defaultCompleted);
  const [animate, setAnimate] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();

  const { t } = useTranslation();

  useEffect(() => {
    setCompleted(defaultCompleted);
  }, [defaultCompleted]);

  const onComplete = useCallback(() => {
    dispatch(completeTask(taskId)).then((action) => {
      if (typeof externalOnComplete === "function") {
        externalOnComplete(action);
      }
    });
  }, [dispatch, taskId, externalOnComplete]);

  const onReOpen = useCallback(() => {
    dispatch(openTask(taskId));
  }, [dispatch, taskId]);

  const makeMagic = useCallback(
    (value) => {
      setAnimate(true);
      setCompleted(value);
      value ? onComplete() : onReOpen();
    },
    [onComplete, onReOpen]
  );

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (disabled) {
        return;
      }

      if (!completed && (parentDependenciesCount > 0 || openSubtasksCount > 0)) {
        setShowConfirm(true);
      } else {
        makeMagic(!completed);
      }
    },
    [completed, disabled, makeMagic, openSubtasksCount, parentDependenciesCount]
  );

  const handleSubmitPrompt = useCallback(() => {
    setShowConfirm(false);
    makeMagic(!completed);
  }, [completed, makeMagic]);

  const handleCancelPrompt = useCallback(() => {
    setShowConfirm(false);
  }, []);

  const description = useMemo(() => {
    return (
      <>
        {t("There are tasks or subtasks that should be finished before this task can be marked as done")}
        <br />
        <br />
        {parentDependenciesCount > 0 ? (
          <b>
            {t("{{count}} predecessor", { count: parentDependenciesCount })}
            <br />
          </b>
        ) : null}
        {openSubtasksCount > 0 ? (
          <>
            <b>{t("{{count}} subtask", { count: openSubtasksCount })}</b>
            <br />
            <br />
            {t(
              "Reopening the task will also automatically reopen all subtasks previously closed by completing the task."
            )}
          </>
        ) : null}
      </>
    );
  }, [openSubtasksCount, parentDependenciesCount, t]);

  return (
    <Fragment>
      <CompleteCheckbox
        onClick={handleClick}
        completed={completed}
        animate={animate}
        disabled={disabled}
        className={className}
      />
      <ConfirmDialog
        open={showConfirm}
        dialogTitle={t("Mark as Done?")}
        dialogContent={description}
        confirmBtnText={t("Ignore and Mark as Done")}
        cancelBtnText={t("Cancel")}
        onConfirm={handleSubmitPrompt}
        onCancel={handleCancelPrompt}
      />
    </Fragment>
  );
};

TaskCompleteCheckbox.propTypes = {
  taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  defaultCompleted: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  parentDependenciesCount: PropTypes.number,
  openSubtasksCount: PropTypes.number,
  onComplete: PropTypes.func,
};

TaskCompleteCheckbox.defaultProps = {
  defaultCompleted: false,
  disabled: false,
  parentDependenciesCount: 0,
  openSubtasksCount: 0,
};

TaskCompleteCheckbox.displayName = `TaskCompleteCheckbox`;

export default TaskCompleteCheckbox;
